import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import useSEO from '../hooks/useSEO';
import TopNav from './TopNav';
import SolutionsFooter from './SolutionsFooter';

function ArticlePage({ 
  category, 
  title, 
  subtitle, 
  heroImage,
  heroImageAspectRatio = '16/9',
  heroImageObjectFit = 'cover',
  articleContent,
  relatedArticles,
  intent = 'general'
}) {
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();

  // SEO meta tags
  useSEO({
    title,
    description: subtitle,
    image: heroImage,
    url: `https://veydra.io${location.pathname}`,
    type: 'article'
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await addDoc(collection(db, 'form-submissions-incremental'), {
        ...formData,
        source: 'article-subscription',
        page: location.pathname,
        articleTitle: title,
        intent: intent,
        submittedAt: serverTimestamp()
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <TopNav />

      <main className="max-w-5xl mx-auto px-8 py-16 md:py-24">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <Link to="/innovation" className="text-sm text-gray-500 hover:text-gray-700">← Research & Innovation</Link>
        </nav>

        {/* Hero */}
        <div className="mb-10">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{category}</span>
          <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mt-2 mb-4 leading-tight tracking-tight">{title}</h1>
          <p className="text-lg text-gray-600 leading-relaxed">{subtitle}</p>
        </div>

        {/* Hero Image */}
        {heroImage && (
          <div className="mb-12 relative bg-gray-100 rounded-lg overflow-hidden" style={{ aspectRatio: heroImageAspectRatio }}>
            <img 
              src={heroImage} 
              alt={title} 
              className={`w-full h-full rounded-lg ${heroImageObjectFit === 'contain' ? 'object-contain' : 'object-cover'}`}
              loading="eager"
            />
          </div>
        )}

        {/* Article + Sidebar Layout */}
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Article Content */}
          <div className="prose prose-gray max-w-none flex-1 lg:max-w-2xl">
            {articleContent}
          </div>

          {/* Sidebar: Lead Capture */}
          <div className="lg:w-72 flex-shrink-0">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 lg:sticky lg:top-8">
              {!submitted ? (
                <>
                  <h3 className="text-base font-medium text-gray-900 mb-2">Stay in the loop</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Get occasional updates on research and new thinking.
                  </p>
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="Your name"
                    />
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="your@email.com"
                    />
                    <button
                      type="submit"
                      className="w-full bg-gray-900 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
                    >
                      Subscribe
                    </button>
                  </form>
                  {error && (
                    <p className="text-xs text-red-600 mt-2 text-center">{error}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    No spam. Unsubscribe anytime.
                  </p>
                </>
              ) : (
                <div className="text-center py-2">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-gray-900">You're on the list</p>
                  <p className="text-xs text-gray-500 mt-1">We'll be in touch.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles && relatedArticles.length > 0 && (
          <div className="pt-12 mt-12 border-t border-gray-200">
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6">Related</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedArticles.map((article, index) => (
                <Link key={index} to={article.href} className="group">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{article.category}</p>
                  <p className="text-gray-900 group-hover:text-gray-600 font-medium">{article.title} →</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>

      <SolutionsFooter />
    </div>
  );
}

export default ArticlePage;
